/**
 * Fertile window derivation, per NFP method.
 *
 * The window is bounded by gamete survival, not by calendar days: sperm can survive several
 * days in fertile-type mucus, and the ovum is viable for roughly a day after ovulation. So a
 * window opens on the first sign that fertile-type conditions have begun (mucus onset, a
 * rising monitor reading) and closes a fixed number of days after ovulation, which each
 * method identifies through its own Peak Day rule.
 *
 * A calendar day (Döring's shortest cycle − 20, or the conventional first-5-days rule) is
 * only ever a floor for how late the window may open when no biological sign has appeared
 * yet — never the definition of the window itself.
 *
 * Each method's rules as implemented here:
 *
 * - Creighton / Billings (mucus-primary): opens at the start of the mucus patch — the first
 *   day any mucus is observed after menstruation. Closes 3 full days after Peak Day, so the
 *   4th day post-Peak is infertile.
 * - Marquette (monitor-primary): opens on the protocol's calculated start day — Day 6, or the
 *   lowest of the last six cycles − 20 once that much history exists. The monitor's role is
 *   identifying Peak and closing the window, not opening it, so a High reading only moves the
 *   start earlier in the unusual case that one appears before the calculated day. Closes 3
 *   full days after the Peak reading.
 * - Sympto-Thermal (double-check): opens at the earlier of mucus onset and the calendar
 *   floor. Closes at the later of Peak + 3 and the third temperature above the cover line —
 *   both signs must have confirmed before the window is treated as closed.
 *
 * With several methods selected the most conservative bound wins in each direction: the
 * earliest opening and the latest closing of any selected method.
 *
 * This module describes what the observations show. It is not medical advice, and the
 * conservative-by-default choices here exist so the display never claims a fertile window
 * closed earlier than the recorded data supports.
 */

import {
  findLhSurgeDay,
  getPeakClose,
  isPeakMucus,
  normalizeMonitor,
  usesExpandedMucus,
  usesMonitor,
  usesSymptoThermal,
  type NFPMethod,
} from "./cycleLogic";

/** Days after Peak Day that remain fertile; the following day is treated as infertile. */
export const POST_PEAK_FERTILE_DAYS = 3;

/**
 * Days before ovulation that sperm survival makes fertile, plus the day after ovulation the
 * ovum stays viable. Used only for the ovulation-anchored estimate, not for the method rules.
 */
export const PRE_OVULATION_FERTILE_DAYS = 5;
export const POST_OVULATION_FERTILE_DAYS = 1;

/**
 * The standard calculated start of the fertile window: Day 6, the conventional
 * "first five days are infertile" allowance. Superseded by the shortest-cycle − 20
 * calculation once enough cycle history exists for it.
 */
export const DEFAULT_CALENDAR_START_CD = 6;

/**
 * Cycles of history the shortest − 20 calculation needs. The rule is defined over the last
 * six cycles; applying it to one or two would let a single unusually short cycle pull the
 * window's start earlier than the record supports.
 */
export const SHORTEST_CYCLE_RULE_MIN_CYCLES = 6;

export type WindowStartBasis =
  | "mucus-onset"
  | "monitor-high"
  | "shortest-cycle-minus-20"
  | "protocol-day-6"
  | "postpartum"
  | "start-not-recorded";

export type WindowEndBasis =
  | "peak-plus-3"
  | "thermal-double-check"
  | "awaiting-thermal-shift"
  | "unresolved";

export interface FertileWindowObservation {
  cycle_day: number;
  bleeding?: number | null;
  monitor?: string | null;
  mucus?: string | null;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  temperature_f?: number | null;
  ovulation_test?: string | null;
}

export interface FertileWindow {
  startCd: number;
  startBasis: WindowStartBasis;
  /** Null when no Peak Day has been identified, so the closing sign hasn't appeared. */
  endCd: number | null;
  endBasis: WindowEndBasis;
  peakCd: number | null;
  /**
   * First cycle day with a positive ovulation test (OPK), if any. Corroborating display only:
   * no method defines Peak Day by an LH strip, so this never opens or closes the window.
   */
  lhSurgeCd: number | null;
  /**
   * Ovulation estimated from Peak Day. A monitor Peak marks the LH surge, which precedes
   * ovulation by roughly a day; a mucus Peak Day is most often the day before ovulation too.
   * Either way this is an estimate from a proxy sign, never a measured ovulation date.
   * With no Peak yet, a positive OPK stands in as the same +1 proxy — the window itself
   * stays unresolved until a method Peak closes it.
   */
  estimatedOvulationCd: number | null;
}

export interface FertileWindowOptions {
  peakCd?: number | null;
  /** Shortest completed cycle on record, for the shortest − 20 calculation. */
  shortestCycleLength?: number | null;
  /** Completed cycles on record; the shortest − 20 rule needs six of them. */
  completedCycleCount?: number;
  /** Postpartum cycles have no established infertile pre-ovulatory phase. */
  isPostpartum?: boolean;
  /**
   * The cycle's real CD 1 was never recorded (a partial cycle — see derivedCycles.ts), so its
   * day numbers are offsets from the first logged day. No calendar rule can be applied to
   * them, and there's no known infertile pre-ovulatory phase to claim.
   */
  startInferred?: boolean;
}

/** Any mucus at all, fertile-type or not — the start of the mucus patch. */
const hasAnyMucus = (obs: FertileWindowObservation): boolean => {
  const consistency = (obs.mucus_consistency || "").toLowerCase();
  const legacy = (obs.mucus || "").toLowerCase();
  const color = (obs.mucus_color || "").toLowerCase();

  const isDry = (value: string) => value === "dry" || value === "none" || value === "";

  if (!isDry(consistency)) return true;
  // Only trust the legacy free-text field when no structured consistency was recorded, so a
  // day explicitly marked dry isn't overridden by stale text.
  if (!consistency && !isDry(legacy)) return true;
  return Boolean(color) && color !== "none";
};

/**
 * The protocol's calculated start day: shortest − 20 once six cycles of history exist,
 * otherwise Day 6. When the calculation lands LATER than Day 6 the earlier day still wins —
 * a later start would claim infertility on days the calculation alone doesn't establish.
 */
const getCalculatedStart = (
  shortestCycleLength?: number | null,
  completedCycleCount = 0
): { cd: number; basis: WindowStartBasis } => {
  const fallback = {
    cd: DEFAULT_CALENDAR_START_CD,
    basis: "protocol-day-6" as WindowStartBasis,
  };

  if (!shortestCycleLength || completedCycleCount < SHORTEST_CYCLE_RULE_MIN_CYCLES) {
    return fallback;
  }

  const calculated = shortestCycleLength - 20;
  if (calculated < DEFAULT_CALENDAR_START_CD) {
    return { cd: Math.max(1, calculated), basis: "shortest-cycle-minus-20" };
  }
  return fallback;
};

/** A monitor reading above Low. */
const isRaisedMonitor = (obs: FertileWindowObservation | undefined): boolean => {
  const monitor = normalizeMonitor(obs?.monitor);
  return monitor === "high" || monitor === "peak";
};

/** Last day of the menstrual run, so mucus onset isn't looked for during bleeding. */
const getLastMensesDay = (observations: FertileWindowObservation[]): number => {
  const byDay = new Map(observations.map((obs) => [obs.cycle_day, obs]));
  let last = 0;
  for (let cd = 1; byDay.get(cd)?.bleeding; cd += 1) {
    last = cd;
  }
  return last;
};

/**
 * The method-defined fertile window for one cycle. Bounds are returned in cycle days;
 * callers clamp them to the days a cycle actually has.
 */
export const computeFertileWindow = (
  observations: FertileWindowObservation[],
  methods: NFPMethod[] = ["Marquette"],
  options: FertileWindowOptions = {}
): FertileWindow => {
  const sorted = [...observations].sort((a, b) => a.cycle_day - b.cycle_day);
  const peakCd = options.peakCd ?? null;
  const lhSurgeCd = findLhSurgeDay(sorted);
  const calculatedStart = getCalculatedStart(
    options.shortestCycleLength,
    options.completedCycleCount
  );

  const candidates: { cd: number; basis: WindowStartBasis }[] = [];

  // Postpartum: cycles resume without a reliable infertile pre-ovulatory phase, so no
  // calendar allowance applies and the window is open from the start (matching how
  // analyzeCycle already treats postpartum days).
  if (options.startInferred) {
    // Unknown start: Day 6 / shortest − 20 would be counted from an arbitrary day, so the
    // only conservative reading is that the window may already be open from the first day
    // logged — the same treatment postpartum days get.
    candidates.push({ cd: 1, basis: "start-not-recorded" });
  } else if (options.isPostpartum) {
    candidates.push({ cd: 1, basis: "postpartum" });
  } else {
    const lastMensesDay = getLastMensesDay(sorted);
    const postMenses = sorted.filter((obs) => obs.cycle_day > lastMensesDay);

    // Mucus-primary methods open the window at the start of the mucus patch. Sympto-Thermal
    // uses mucus as its first of two signs, so it opens on mucus onset too.
    const usesMucusRule = usesExpandedMucus(methods) || usesSymptoThermal(methods);
    const mucusOnset = usesMucusRule ? postMenses.find(hasAnyMucus) : undefined;
    if (mucusOnset) {
      candidates.push({ cd: mucusOnset.cycle_day, basis: "mucus-onset" });
    }

    // A calculated start day belongs to the methods that actually teach one — Marquette's
    // Day 6 protocol and Sensiplan's minus-20 rule. Creighton and Billings define the window
    // by the mucus patch alone, so for a mucus-only selection it's a fallback for when no
    // mucus was recorded at all, never a competing earlier opening.
    const usesCalculatedStart = usesMonitor(methods) || usesSymptoThermal(methods);
    if (usesCalculatedStart || !mucusOnset) {
      candidates.push(calculatedStart);
    }

    // The monitor identifies Peak and closes the window; it isn't what opens it. So a High
    // reading only counts as an opening when it appears BEFORE the calculated start day —
    // and then only if it's sustained, since a lone High between Lows is more often a
    // one-off reading than a genuinely early rise.
    if (usesMonitor(methods)) {
      const earlyRise = postMenses.find((obs, index) => {
        if (obs.cycle_day >= calculatedStart.cd) return false;
        if (!isRaisedMonitor(obs)) return false;
        return isRaisedMonitor(postMenses[index + 1]);
      });
      if (earlyRise) {
        candidates.push({ cd: earlyRise.cycle_day, basis: "monitor-high" });
      }
    }
  }

  // Earliest opening across every applicable rule — the conservative bound.
  const start = candidates.reduce((earliest, candidate) =>
    candidate.cd < earliest.cd ? candidate : earliest
  );

  if (!peakCd) {
    return {
      startCd: Math.max(1, start.cd),
      startBasis: start.basis,
      endCd: null,
      endBasis: "unresolved",
      peakCd: null,
      lhSurgeCd,
      estimatedOvulationCd: lhSurgeCd ? lhSurgeCd + 1 : null,
    };
  }

  let endCd = peakCd + POST_PEAK_FERTILE_DAYS;
  let endBasis: WindowEndBasis = "peak-plus-3";

  // Sympto-Thermal's double check and the mucus methods' count (returning peak-type mucus
  // restarts it) — the same rule the post-Peak advice uses (getPeakClose). Until it can
  // close, the window stays open.
  if (usesSymptoThermal(methods) || usesExpandedMucus(methods)) {
    const close = getPeakClose(sorted, methods, peakCd);
    if (close.lastFertileCd === null) {
      return {
        startCd: Math.max(1, start.cd),
        startBasis: start.basis,
        endCd: null,
        endBasis: close.basis === "awaiting-thermal-shift" ? "awaiting-thermal-shift" : "unresolved",
        peakCd,
        lhSurgeCd,
        estimatedOvulationCd: peakCd + 1,
      };
    }
    endCd = close.lastFertileCd;
    endBasis = close.basis === "thermal-double-check" ? "thermal-double-check" : "peak-plus-3";
  }

  return {
    startCd: Math.max(1, start.cd),
    startBasis: start.basis,
    endCd,
    endBasis,
    peakCd,
    lhSurgeCd,
    estimatedOvulationCd: peakCd + 1,
  };
};

/**
 * Fertile window implied purely by gamete survival around an estimated ovulation day —
 * ovulation − 5 through ovulation + 1. Independent of method protocol; used for the
 * biological estimate shown alongside the method window, never for guidance.
 */
export const getOvulationAnchoredWindow = (
  estimatedOvulationCd: number | null
): { startCd: number; endCd: number } | null => {
  if (!estimatedOvulationCd) return null;
  return {
    startCd: Math.max(1, estimatedOvulationCd - PRE_OVULATION_FERTILE_DAYS),
    endCd: estimatedOvulationCd + POST_OVULATION_FERTILE_DAYS,
  };
};

/** Short human-readable explanation of why a window opened where it did. */
export const describeWindowStart = (basis: WindowStartBasis): string => {
  switch (basis) {
    case "mucus-onset":
      return "First mucus observed";
    case "monitor-high":
      return "Early sustained High monitor reading";
    case "shortest-cycle-minus-20":
      return "Shortest of last 6 cycles − 20";
    case "protocol-day-6":
      return "Day 6 (standard protocol start)";
    case "postpartum":
      return "Postpartum — no infertile pre-ovulatory phase";
    case "start-not-recorded":
      return "Cycle start not recorded";
  }
};

/** Short human-readable explanation of why a window closed where it did. */
export const describeWindowEnd = (basis: WindowEndBasis): string => {
  switch (basis) {
    case "peak-plus-3":
      return "3 full days after Peak Day";
    case "thermal-double-check":
      return "3rd temperature above the cover line";
    case "awaiting-thermal-shift":
      return "Peak Day found; waiting for the temperature shift to confirm it";
    case "unresolved":
      return "No Peak Day identified yet";
  }
};

/**
 * True once a cycle day is past the method's computed fertile window (computeFertileWindow's
 * endCd). Never true while the window is unresolved. Prefer isPostPeakPhaseDay when phase
 * spans are at hand — it also stops at an unmarked next period.
 */
export const isAfterFertileWindow = (
  cycleDay: number,
  windowEndCd: number | null | undefined
): boolean => windowEndCd != null && cycleDay > windowEndCd;

